import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TimelineContext } from "../../../contexts/TimelineContext";
import { createSubmission } from "../../../modules/api";
import { updatePageTitle } from "../../../modules/legacyUtils";
import { generateDisplayListActions } from "../../../modules/timelines";
import { trackEvent } from "../../../modules/tracking";
import { AnalyticsEvents } from "../../../modules/analyticsEvents";
import Lead from "../../display/Lead";
import ConfigurableDisplayList from "../../display/ConfigurableDisplayList";
import CISPromoBanner from "../../display/CISPromoBanner";
import ActionHeader from "../../interactive/ActionHeader";
import FlashMessage from "../../display/FlashMessage";
import { BulletList } from 'react-content-loader';
import AddressFormPage from "./AddressFormPage";
import helperTextData from "../../../data/helperTextData.json";
import { redirectTo } from "../../../modules/utils";
export default function AddressesIndex({ addresses, isCIS = false, startDate, lead, gaps, authenticityToken, invalidateResource }) {
    const [error, setError] = useState(null);
    const hasTrackedView = useRef(false);
    const location = useLocation();
    const { setOnNextClick, setDisableNextButton, setHideNextButton, setHideTimeline } = React.useContext(TimelineContext);
    useEffect(() => { if (hasTrackedView.current)
        return; hasTrackedView.current = true; updatePageTitle('About your addresses'); trackEvent(AnalyticsEvents.screenViews.addresses()); }, []);
    useEffect(() => {
        if (isCIS) { // Disable the "Next" button if there are no addresses
            setDisableNextButton(!addresses || addresses.length === 0);
            setOnNextClick(() => submit);
        }
        return () => {
            setDisableNextButton(null);
            setOnNextClick(null);
        };
    }, [isCIS, addresses]);
    useEffect(() => {
        // (no addresses yet), there's no "next step" to take or timeline to show until
        // an address exists.
        const noAddresses = !addresses || addresses.length === 0;
        setHideNextButton(noAddresses);
        setHideTimeline(noAddresses);
        return () => { setHideNextButton(false); setHideTimeline(false); };
    }, [addresses]);
    const resourcesLoaded = !!addresses;
    const list = useMemo(() => { const rawList = generateDisplayListActions(addresses, gaps, ['preview']); return rawList.map(item => item.href ? { ...item, asRouterLink: true, linkState: { prevPathname: location.pathname } } : item); }, [addresses, gaps, location.pathname]);
    const helperText = isCIS ? helperTextData.addresses.where_you_lived_cis : helperTextData.addresses.where_you_lived;
    const actionHeaderText = `${startDate.format('MMMM YYYY')} - Now`;
    const submit = () => {
        createSubmission().then(async (data) => {
            // which isn't a part of the timelines
            await trackEvent(AnalyticsEvents.forms.submittedAddress());
            redirectTo(data.redirectLocation);
        }).catch(error => { const errorMessage = error?.error || 'There was a problem submitting your information. Please try again.'; console.error('Error submitting claim:', errorMessage); setError(errorMessage); });
    };
    const renderBody = () => {
        if (!resourcesLoaded) {
            return /*#__PURE__*/ React.createElement(BulletList, { style: { width: '66.6%' } });
        }
        if (addresses.length === 0) { // Skip the timeline-gap "What is your current home address?" prompt entirely -
            // go straight to the add-address form, pre-filled with the timeline's start date.
            return /*#__PURE__*/ React.createElement(AddressFormPage, { authenticityToken: authenticityToken, onSaved: () => invalidateResource?.('addresses', 'timelineGaps'), embedded: true, initialSearch: `?started_at=${startDate.format('YYYY-MM-DD')}` });
        }
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ActionHeader, { text: actionHeaderText }), /*#__PURE__*/ React.createElement(ConfigurableDisplayList, { list: list, placeholderName: "addresses", small: true }));
    };
    return /*#__PURE__*/ React.createElement(Fragment, null, error && /*#__PURE__*/ React.createElement(FlashMessage, { type: "failure", messageList: [error] }), /*#__PURE__*/ React.createElement(CISPromoBanner, null), /*#__PURE__*/ React.createElement(Lead, { dangerouslySetInnerHTML: { __html: lead }, helperText: helperText }), /*#__PURE__*/ React.createElement("hr", null), renderBody());
}
